'use client'

import { Button, Spacer } from '@vinted/web-ui'

import { TRENDSALES_FAQ_ENTRY_ID } from '@marketplace-web/help-center-support-tickets/help-center-trendsales-payout-feature'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { navigateToPage } from '@marketplace-web/shared/browser-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { clickEvent } from '@marketplace-web/help-center/help-center-faq-entry-data'
import { TrackingChannel } from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import {
  ComplaintType,
  DELIVERY_COMPLAINT_FAQ_IDS,
} from '@marketplace-web/complaints/complaints-data'

import {
  COMPLAINT_CREATION_URL,
  NEW_COMPLAINTS_URL,
  NEW_HELP_TICKET_URL,
  TRENDSALES_PAYOUT_FORM_URL,
} from '../constants/routes'

type Props = {
  faqEntryId: number
  transactionId?: number
  proceedButton?: string | null
  isComplaintsFaq?: boolean
  isNewComplaintReportView?: boolean
}

const HelpCenterContactLink = ({
  faqEntryId,
  transactionId,
  proceedButton,
  isComplaintsFaq,
  isNewComplaintReportView,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('help_center.contact_link')
  const isTrendsalesFaq = faqEntryId === TRENDSALES_FAQ_ENTRY_ID
  const isTrendSalesFormEnabled = useFeatureSwitch('trendsales_form')

  const { searchParams, relativeUrl } = useBrowserNavigation()
  const { related_only, thread_id } = searchParams

  const trackButtonClick = () => {
    const targetDetails = {
      transaction_id: transactionId,
      faq_entry_id: faqEntryId,
    }

    track(
      clickEvent({
        screen: 'contact_support',
        target: 'submit',
        targetDetails: JSON.stringify(targetDetails),
        env: 'js',
        path: relativeUrl,
      }),
    )
  }

  const getComplaintType = () => {
    if (DELIVERY_COMPLAINT_FAQ_IDS.includes(faqEntryId)) return ComplaintType.Delivery

    return ComplaintType.Snad
  }

  const getContactFormUrl = () => {
    if (isNewComplaintReportView)
      return urlWithParams(COMPLAINT_CREATION_URL, { type: getComplaintType() })

    if (isComplaintsFaq) return NEW_COMPLAINTS_URL

    if (isTrendsalesFaq && isTrendSalesFormEnabled) return TRENDSALES_PAYOUT_FORM_URL

    return NEW_HELP_TICKET_URL
  }

  const handleContactClick = () => {
    const pageUrl = urlWithParams(getContactFormUrl(), {
      ch: TrackingChannel.HelpCenter,
      faq_entry_id: faqEntryId,
      transaction_id: transactionId,
      related_only: !!related_only,
      thread_id,
      allow_direct: 1,
    })

    trackButtonClick()
    navigateToPage(pageUrl)
  }

  const renderContactLink = () => {
    const title = proceedButton || translate('title')

    return (
      <Button
        onClick={handleContactClick}
        text={title}
        size={Button.Size.Medium}
        styling={Button.Styling.Filled}
        testId="contact-button"
        inline
      />
    )
  }

  return (
    <>
      <Spacer size={Spacer.Size.XLarge} />
      {renderContactLink()}
    </>
  )
}

export default HelpCenterContactLink
